import type { ReactElement } from 'react';
import { PROJECT_DATA } from './data-projects.tsx';
import { BlogRoutes, ProjectRoutes } from './routes.tsx';

export interface KeyTakeaway {
  title: string;
  content: string;
}

export interface BlogPostData {
  title: string;
  excerpt: string;
  author?: string;
  publishDate?: string;
  readTimeMinutes: number;
  image: string | ReactElement;
  keyTakeaways: KeyTakeaway[];
  content: string;
}

const projectToBlog = function<R extends ProjectRoutes>(project: R, data: { content: string, publishDate?: string, featured?: boolean }): Record<R, BlogPostData> {
  const projectData = PROJECT_DATA[project];

  return {
    [project]: {
      title: projectData.title,
      excerpt: projectData.description || '',
      readTimeMinutes: 5,
      image: projectData.showcase,
      keyTakeaways: projectData.keyTakeaways,
      content: data.content,
      publishDate: data.publishDate,
    },
  } as Record<R, BlogPostData>;
};

export const BLOG_DATA: Record<BlogRoutes, BlogPostData> = {
    ...projectToBlog(ProjectRoutes.INDUSTRIAL_TECHNICIANS, {
      content: '/blog-content/industrial_technicians.md',
    }),
    [BlogRoutes.AI_IN_AEROSPACE_BUILDING_SOVEREIGN_KNOWLEDGE_SYSTEMS]: {
      title: 'AI in Aerospace: Building Sovereign AI Systems',
      excerpt: 'Aerospace organizations face unique security and sovereignty constraints. Purpose-built knowledge systems within organizational infrastructure enable AI capabilities while respecting data sovereignty requirements.',
      readTimeMinutes: 8,
      image: '/blog-content/ai_in_aerospace_building_sovereign_knowledge_systems/poster.jpg',
      keyTakeaways: [
        {
            'title': 'Data Sovereignty as Fundamental Requirement',
            'content': 'Aerospace cannot send technical data to external AI services due to ITAR, air-gapped environments, and competitive concerns. AI systems must operate within organizational infrastructure.'
        },
        {
            'title': 'On-Premises Deployment Enables AI Capabilities',
            'content': 'Single-tenant systems within aerospace infrastructure make flight test data, prototype insights, and maintenance expertise accessible while maintaining security boundaries.'
        },
        {
            'title': 'Capturing Expertise from Complex Programs',
            'content': 'Beyond accessing documentation, these systems preserve operational knowledge from prototype builds, flight tests, and MRO operations, building institutional expertise across program transitions.'
        },
        {
            'title': 'Accessing Information in Complex Technical Documents',
            'content': 'Aerospace documentation contains critical information in diagrams and schematics alongside text which, which must be understood by AI system to make sense of the information available.'
        }
      ],
      content: '/blog-content/ai_in_aerospace_building_sovereign_knowledge_systems.md',
    },
    [BlogRoutes.IS_THERE_A_WAY_PAST_KNOWLEDGE_FRICTION_IN_AUTOMOTIVE_OPERATIONS]: {
      title: 'Is There a Way Past Knowledge Friction in Automotive Operations?',
      excerpt: 'Leading automotive manufacturers are leveraging technology to transform fragmented technical knowledge into a strategic advantage, improving efficiency and reducing costs in operations.',
      readTimeMinutes: 8,
      image: '/blog-content/is_there_a_way_past_knowledge_friction_in_automotive_operations/automotive_floor_collab.jpg',
      keyTakeaways: [
        {
              'title': 'Transforming Technical Knowledge',
              'content': 'Leading automotive manufacturers are leveraging technology to convert fragmented technical documentation into a strategic advantage, enhancing operational efficiency and reducing costs.'
        },
        {
              'title': 'The Cost of Knowledge Fragmentation',
              'content': 'Knowledge fragmentation leads to inefficiencies in service networks and engineering centers, causing delays and lost revenue as technicians and engineers struggle to access crucial information.'
        },
        {
              'title': 'Limitations of Traditional Solutions',
              'content': 'Traditional document management systems alone are inadequate, as they fail to capture the operational knowledge that skilled professionals possess, leading to a reliance on experienced personnel for problem-solving.'
        }
      ],
      content: '/blog-content/is_there_a_way_past_knowledge_friction_in_automotive_operations.md',
    },
    [BlogRoutes.WHY_GENERIC_AI_WONT_POWER_THE_NEXT_INDUSTRIAL_REVOLUTION]: {
      title: 'Why Manufacturing Leaders Move Beyond Generic AI',
      excerpt: 'Facing labor shortages and skill gaps, manufacturers are turning to AI chatbots for help. However, these generic AI tools suffer severe shortcomings when attempting to upskill frontline workers.',
      readTimeMinutes: 6,
      image: '/blog-content/why_generic_ai_wont_power_the_next_industrial_revolution/poster.jpg',
      keyTakeaways: [
        {
          'title': 'Labor Shortages and Skills Gaps',
          'content': 'Manufacturers face significant labor shortages and skill gaps, particularly in frontline roles, as industries like manufacturing, construction, and energy are projected to be heavily impacted by an aging workforce.'
        },
        {
          'title': 'Limitations of Generic AI Tools',
          'content': 'Generic AI chatbots struggle with data reliability, operational knowledge, large files and accuracy in manufacturing contexts, limiting their impact in providing technical support and upskilling frontline workers.'
        },
        {
          'title': 'Data Ownership Concerns',
          'content': 'Organizations have limited control over the data stored and processed by mainstream AI tools, raising concerns about data ownership and the potential risks of sensitive information being exposed.'
        },
        {
          'title': 'Operational Knowledge Management',
          'content': 'Capturing, and more importably maintaining, knowledge from expert workers is challenging; specialised AI knowledge management systems offer a solution to simplify this process.'
        },
      ],
      content: '/blog-content/why_generic_ai_wont_power_the_next_industrial_revolution.md'
    },
    ...projectToBlog(ProjectRoutes.CAPMO_AI, {
      content: '/blog-content/capmo_ai.md',
    }),
};

export const FEATURED_BLOG_POST: BlogRoutes = BlogRoutes.INDUSTRIAL_TECHNICIANS;

export const getBlogPostsPage = (page: number, perPage: number = 6): BlogRoutes[] => {
  const allPosts = (Object.keys(BLOG_DATA) as BlogRoutes[]).filter(slug => slug !== FEATURED_BLOG_POST);
  const start = (page - 1) * perPage;
  const end = start + perPage;
  return allPosts.slice(start, end);
};

export const getTotalPages = (perPage: number = 6): number => {
  const allPosts = (Object.keys(BLOG_DATA) as BlogRoutes[]).filter(slug => slug !== FEATURED_BLOG_POST);
  return Math.ceil(allPosts.length / perPage);
};

export const getRelatedPosts = (currentSlug: BlogRoutes, count: number = 3): BlogRoutes[] => {
  const allPosts = Object.keys(BLOG_DATA).filter(slug => slug !== currentSlug) as BlogRoutes[];
  return allPosts.slice(0, count);
};
